// PurchaseOrderApprovalPage.js
// Página de Aprovação de Pedidos de Compra
// VERSION: 3.3 - With Notifications and Print

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import useAuth from '../../hooks/useAuth';
import { doc, getDoc, getDocs, collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';
import ConfirmDialog from '../../components/common/ConfirmDialog/ConfirmDialog';
import Toast from '../../components/common/Toast/Toast';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import ViewOrderModal from '../../components/common/ViewOrderModal/ViewOrderModal';
import TrackingModal from '../../components/common/TrackingModal/TrackingModal';
import ConfirmOrderModal from '../../components/common/ConfirmOrderModal/ConfirmOrderModal';
import styles from './Orders.module.css';

// React Icons
import {
    FiX,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiMapPin,
    FiList,
    FiEye,
    FiXCircle,
    FiCheckCircle,
    FiArrowLeft,
    FiRefreshCw,
    FiTrendingUp,
    FiClipboard,
    FiPrinter
} from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';
import { FiDollarSign } from 'react-icons/fi';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const PURCHASE_ORDERS_COLLECTION = 'purchase-orders';
const ITEMS_PER_PAGE = 10;

const ORDER_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    REJECTED: 'rejected'
};

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.PENDING]: { label: 'Pendente', className: 'statusBadgePending' },
    [ORDER_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [ORDER_STATUS.COMPLETED]: { label: 'Concluído', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' },
    [ORDER_STATUS.REJECTED]: { label: 'Rejeitado', className: 'statusBadgeRejected' }
};

const DELIVERY_TYPES = [
    { value: 'transportadora', label: 'Na transportadora' },
    { value: 'retirar', label: 'À retirar' },
    { value: 'nosso_endereco', label: 'Nosso endereço' },
    { value: 'triangular', label: 'Triangular' }
];

const FREIGHT_BY_OPTIONS = [
    { value: 'emitente', label: 'Emitente' },
    { value: 'destinatario', label: 'Destinatário' }
];

const statusFilterOptions = [
    { value: ORDER_STATUS.PENDING, label: 'Pendentes' },
    { value: ORDER_STATUS.APPROVED, label: 'Aprovados' },
    { value: ORDER_STATUS.REJECTED, label: 'Rejeitados' },
    { value: ORDER_STATUS.COMPLETED, label: 'Concluídos' },
    { value: ORDER_STATUS.CANCELLED, label: 'Cancelados' },
    { value: '', label: 'Todos' }
];

// Helper functions
const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

// ============================================
// 2. APPROVAL MODAL
// ============================================
const ApprovalModal = ({ order, onClose, onConfirm }) => {
    const [approvalNotes, setApprovalNotes] = useState('');
    const [isRejection, setIsRejection] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!order) return null;

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm({
                orderId: order.id,
                orderCode: order.code,
                status: isRejection ? ORDER_STATUS.REJECTED : ORDER_STATUS.APPROVED,
                notes: approvalNotes,
                approvedBy: 'Aprovador',
                approvedAt: new Date().toLocaleString('pt-BR')
            });
            onClose();
        } catch (error) {
            // Error handled by parent
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerSmall}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>
                            {isRejection ?
                                <FiXCircle size={24} style={{ marginRight: '10px', color: '#dc3545' }} /> :
                                <FiCheckCircle size={24} style={{ marginRight: '10px', color: '#28a745' }} />
                            }
                            {isRejection ? 'Rejeitar Pedido' : 'Aprovar Pedido'}
                        </h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {order.supplierName}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={loading}
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiTrendingUp size={18} /></div>
                            <h3 className={styles.sectionTitle}>Resumo do Pedido</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <label>Valor Total:</label>
                                <span style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                    {formatCurrency(order.calculatedValues?.total || 0)}
                                </span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>Quantidade de Itens:</label>
                                <span>{order.items?.length || 0}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>Data do Pedido:</label>
                                <span>{order.orderDate}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>Solicitante:</label>
                                <span>{order.responsible}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                            <h3 className={styles.sectionTitle}>
                                {isRejection ? 'Motivo da Rejeição' : 'Observações (opcional)'}
                            </h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <textarea
                                value={approvalNotes}
                                onChange={(e) => setApprovalNotes(e.target.value)}
                                className={styles.textarea}
                                rows={4}
                                placeholder={isRejection ?
                                    "Informe o motivo da rejeição do pedido..." :
                                    "Adicione observações sobre a aprovação (opcional)..."
                                }
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={() => setIsRejection(!isRejection)}
                        className={styles.secondaryButton}
                        disabled={loading}
                    >
                        {isRejection ? <FiArrowLeft size={18} /> : <FiXCircle size={18} />}
                        {isRejection ? 'Voltar para Aprovar' : 'Rejeitar Pedido'}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={isRejection ? styles.dangerButton : styles.confirmButton}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                Processando...
                            </>
                        ) : (
                            <>
                                {isRejection ? <FiXCircle size={18} /> : <FiCheck size={18} />}
                                {isRejection ? 'Confirmar Rejeição' : 'Aprovar Pedido'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// 3. MAIN COMPONENT
// ============================================
const PurchaseOrderApprovalPage = () => {
    const can = usePagePermissions('aprovacao_de_pedido_de_compra');

    const [viewingOrder, setViewingOrder] = useState(null);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [approvingOrder, setApprovingOrder] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(ORDER_STATUS.PENDING);
    const [toasts, setToasts] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(null);

    const { currentUser } = useAuth();
    const { addNotification } = useNotifications();
    const { data: orders, loading: ordersLoading, updateDocument, refreshData } = useFirestore(
        PURCHASE_ORDERS_COLLECTION,
        'createdAt',
        true
    );

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // ============================================
    // NOTIFICATION FUNCTION
    // ============================================
    const notifyOrderCreator = async (orderId, status, orderCode) => {
        try {
            // Buscar o pedido para obter o criador
            const orderDoc = await getDoc(doc(db, 'purchase-orders', orderId));
            if (!orderDoc.exists()) return;

            const orderData = orderDoc.data();
            const creatorName = orderData.responsible || orderData.requestedBy;

            if (!creatorName) return;

            // Buscar o usuário criador
            const usersSnapshot = await getDocs(collection(db, 'users'));
            let creatorUser = null;

            usersSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.nome === creatorName || data.email === creatorName) {
                    creatorUser = { uid: doc.id, ...data };
                }
            });

            if (!creatorUser || creatorUser.uid === currentUser?.uid) return;

            const isApproved = status === ORDER_STATUS.APPROVED;
            const module = 'aprovacao_de_pedido_de_compra';
            const config = getModuleConfig(module);

            const notification = {
                id: `po_${status}_${orderId}_${Date.now()}`,
                type: isApproved ? 'purchase_order_approved' : 'purchase_order_rejected',
                module: module,
                moduleIcon: config?.icon || '📋',
                moduleColor: config?.color || '#27ae60',
                moduleName: config?.name || 'Aprovação de Pedido',
                title: isApproved ? '✅ Pedido Aprovado' : '❌ Pedido Rejeitado',
                message: `Seu pedido ${orderCode} foi ${isApproved ? 'aprovado' : 'rejeitado'} por ${currentUser?.nome || 'Aprovador'}`,
                fullMessage: `Detalhes:\n• Pedido: ${orderCode}\n• Status: ${isApproved ? 'Aprovado' : 'Rejeitado'}\n• Aprovado por: ${currentUser?.nome || 'Aprovador'}\n• Observações: ${orderData.approvalNotes || 'N/A'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: isApproved ? 'Alta' : 'Média',
                route: `/pedidos/pedido-de-compra`,
                data: {
                    orderId: orderId,
                    orderCode: orderCode,
                    status: status,
                    approvedBy: currentUser?.nome || 'Aprovador'
                },
                userId: creatorUser.uid
            };

            // Adicionar notificação via contexto
            addNotification(notification);

            // Salvar no Firestore para persistência
            try {
                await addDoc(collection(db, 'notifications'), {
                    userId: creatorUser.uid,
                    ...notification,
                    createdAt: new Date()
                });
            } catch (err) {
                console.warn('Erro ao salvar notificação:', err);
            }

            console.log(`✅ Notificação enviada para ${creatorUser.nome} sobre ${orderCode}`);
        } catch (error) {
            console.error('❌ Erro ao notificar criador do pedido:', error);
        }
    };

    const filteredOrders = useMemo(() => {
        if (!orders) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return orders.filter(order => {
            if (statusFilter && order.status !== statusFilter) return false;

            const matchesSearch = (order.code && order.code.toLowerCase().includes(lowerSearch)) ||
                (order.supplierName && order.supplierName.toLowerCase().includes(lowerSearch));

            return matchesSearch;
        }).sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
        });
    }, [orders, searchTerm, statusFilter]);

    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
    const currentOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    // ============================================
    // APPROVAL HANDLER WITH NOTIFICATION
    // ============================================
    const handleApprovalConfirm = async (approvalData) => {
        setActionLoading(true);

        try {
            const updateData = {
                status: approvalData.status,
                approvalNotes: approvalData.notes,
                approvedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Aprovador',
                approvedAt: new Date(),
                updatedAt: new Date()
            };

            const result = await updateDocument(approvalData.orderId, updateData);

            if (result.success) {
                const actionText = approvalData.status === ORDER_STATUS.APPROVED ? 'aprovado' : 'rejeitado';
                addToast(`Pedido ${approvalData.orderCode} ${actionText} com sucesso!`, 'Success');
                await refreshData();

                // ✅ NOTIFICAR O CRIADOR DO PEDIDO
                await notifyOrderCreator(approvalData.orderId, approvalData.status, approvalData.orderCode);
            } else {
                addToast(`Erro ao aprovar: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setActionLoading(false);
            setApprovingOrder(null);
        }
    };

    // ============================================
    // PRINT FUNCTION
    // ============================================
    const handlePrintOrder = useCallback((order) => {
        if (!order) return;

        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow) {
            addToast('Por favor, permita pop-ups para imprimir.', 'Warning');
            return;
        }

        const escapeHtml = (str) => String(str ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const itemsRows = (order.items || []).map((item, idx) => `
            <tr>
                <td style="border:1px solid #ddd;padding:6px;text-align:center;">${idx + 1}</td>
                <td style="border:1px solid #ddd;padding:6px;"><strong>${escapeHtml(item.code || '')}</strong></td>
                <td style="border:1px solid #ddd;padding:6px;">${escapeHtml(item.name || item.description || '')}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:center;">${item.quantity || 0}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:right;">${formatCurrency(item.unitPrice || 0)}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:right;">${formatCurrency(item.total || (item.quantity * item.unitPrice) || 0)}</td>
            </tr>
        `).join('');

        const total = order.calculatedValues?.total || 0;
        const statusLabel = ORDER_STATUS_LABELS[order.status]?.label || order.status;
        const deliveryLabel = DELIVERY_TYPES.find(d => d.value === order.deliveryType)?.label || order.deliveryType || '-';
        const freightLabel = FREIGHT_BY_OPTIONS.find(f => f.value === order.freightBy)?.label || order.freightBy || '-';

        printWindow.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Pedido de Compra - ${escapeHtml(order.code)}</title>
  <style>
    @page { size: A4 portrait; margin: 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { font-family: Calibri, Arial, sans-serif; font-size: 11px; color: #000; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    th { background: #e8eaf6; border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold; }
    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #1a237e; padding-bottom: 12px; margin-bottom: 16px; }
    .title { font-size: 22px; font-weight: bold; color: #1a237e; letter-spacing: 1px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; padding: 12px; background: #fafafa; border: 1px solid #ddd; }
    .info-item label { font-size: 9px; color: #666; font-weight: bold; display: block; margin-bottom: 2px; }
    .info-item span { font-size: 11px; }
    .totals { font-weight: bold; background: #e8eaf6; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 9px; color: #666; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">PEDIDO DE COMPRA</div>
      <div style="font-size:12px;font-weight:bold;margin-top:4px;">Código: ${escapeHtml(order.code)}</div>
    </div>
    <div style="text-align:right;font-size:10px;">
      <div>Data: ${escapeHtml(order.orderDate || '-')}</div>
      <div>Status: ${escapeHtml(statusLabel)}</div>
    </div>
  </div>

  <div class="info-grid">
    <div class="info-item">
      <label>FORNECEDOR</label>
      <span>${escapeHtml(order.supplierName || 'N/A')}</span>
    </div>
    <div class="info-item">
      <label>RESPONSÁVEL</label>
      <span>${escapeHtml(order.responsible || order.requestedBy || '-')}</span>
    </div>
    <div class="info-item">
      <label>CONDIÇÃO DE PAGAMENTO</label>
      <span>${escapeHtml(order.paymentTerms || order.paymentCondition || '-')}</span>
    </div>
    <div class="info-item">
      <label>TIPO DE ENTREGA</label>
      <span>${escapeHtml(deliveryLabel)}</span>
    </div>
    <div class="info-item">
      <label>FRETE POR</label>
      <span>${escapeHtml(freightLabel)}</span>
    </div>
    <div class="info-item">
      <label>APROVADO POR</label>
      <span>${escapeHtml(order.approvedBy || '-')}${order.approvedAt ? ' em ' + escapeHtml(typeof order.approvedAt === 'object' ? (order.approvedAt.toLocaleDateString ? order.approvedAt.toLocaleDateString('pt-BR') : '') : order.approvedAt) : ''}</span>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:35px;text-align:center;">#</th>
        <th>Código</th>
        <th>Descrição</th>
        <th style="width:60px;text-align:center;">Qtde</th>
        <th style="width:90px;text-align:right;">Valor Unit.</th>
        <th style="width:100px;text-align:right;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${itemsRows}
      <tr class="totals">
        <td colspan="5" style="border:1px solid #ddd;padding:6px;text-align:right;">TOTAL GERAL:</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${formatCurrency(total)}</td>
      </tr>
    </tbody>
  </table>

  ${order.notes ? `<div style="margin-top:16px;"><strong>Observações:</strong><p style="margin-top:4px;padding:8px;background:#f5f5f5;">${escapeHtml(order.notes)}</p></div>` : ''}
  ${order.approvalNotes ? `<div style="margin-top:12px;"><strong>Notas de Aprovação:</strong><p style="margin-top:4px;padding:8px;background:#f5f5f5;">${escapeHtml(order.approvalNotes)}</p></div>` : ''}

  <div class="footer">
    <div style="text-align:right;font-style:italic;">Documento gerado eletronicamente em ${new Date().toLocaleString('pt-BR')}</div>
  </div>

  <script>
    window.onload = function() { window.print(); setTimeout(function(){ window.close(); }, 500); };
  <\/script>
</body>
</html>`);
        printWindow.document.close();
    }, [addToast]);

    const showSkeleton = useMemo(() => {
        if (ordersLoading) return true;
        if (orders === null || orders === undefined) return true;
        return false;
    }, [ordersLoading, orders]);

    const pendingCount = orders?.filter(o => o.status === ORDER_STATUS.PENDING).length || 0;
    const approvedCount = orders?.filter(o => o.status === ORDER_STATUS.APPROVED).length || 0;
    const rejectedCount = orders?.filter(o => o.status === ORDER_STATUS.REJECTED).length || 0;

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={!!confirmDialog}
                title={confirmDialog?.title || 'Confirmar'}
                message={confirmDialog?.message || ''}
                confirmText={confirmDialog?.confirmText || 'Confirmar'}
                cancelText="Cancelar"
                confirmVariant={confirmDialog?.confirmVariant || 'primary'}
                icon={confirmDialog?.icon}
                onConfirm={confirmDialog?.onConfirm || (() => { })}
                onCancel={() => setConfirmDialog(null)}
                isLoading={actionLoading}
            />

            {/* Modals */}
            {viewingOrder && (
                <ViewOrderModal
                    order={viewingOrder}
                    onClose={() => setViewingOrder(null)}
                />
            )}

            {trackingOrder && (
                <TrackingModal
                    order={trackingOrder}
                    onClose={() => setTrackingOrder(null)}
                    onUpdateStatus={async (orderId, status, description) => {
                        addToast(`Status atualizado para ${status}`, 'Success');
                    }}
                />
            )}

            {approvingOrder && (
                <ApprovalModal
                    order={approvingOrder}
                    onClose={() => setApprovingOrder(null)}
                    onConfirm={handleApprovalConfirm}
                />
            )}

            {/* Header */}
            <PageHeader
                title="Aprovação de Pedidos"
                actionText=""
                showAction={false}
            />

            {/* Status Filter Chips */}
            <div className={styles.statusFilterBar}>
                {statusFilterOptions.map(opt => (
                    <button
                        key={opt.value || 'all'}
                        onClick={() => {
                            setStatusFilter(opt.value);
                            setCurrentPage(1);
                        }}
                        className={`${styles.filterChip} ${statusFilter === opt.value ? styles.filterChipActive : ''}`}
                    >
                        {opt.label}
                        {opt.value && (
                            <span style={{ marginLeft: '8px', fontSize: '0.7rem', opacity: 0.8 }}>
                                ({orders?.filter(o => o.status === opt.value).length || 0})
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Código do pedido, fornecedor..."
                    onSearch={(value) => {
                        setSearchTerm(value);
                        setCurrentPage(1);
                    }}
                    initialValue={searchTerm}
                    debounceDelay={300}
                />
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Fornecedor</th>
                            <th>Data</th>
                            <th>Valor Total</th>
                            <th>Status</th>
                            <th>Aprovado por</th>
                            <th style={{ width: '320px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton ? (
                            <tr>
                                <td colSpan="7">
                                    <TableSkeleton columns={7} rows={ITEMS_PER_PAGE} />
                                </td>
                            </tr>
                        ) : currentOrders && currentOrders.length > 0 ? (
                            currentOrders.map(order => (
                                <tr key={order.id} className={styles.tableRow}>
                                    <td><strong>{order.code}</strong></td>
                                    <td>{order.supplierName}</td>
                                    <td>{order.orderDate}</td>
                                    <td className={styles.priceCell}>
                                        {formatCurrency(order.calculatedValues?.total || 0)}
                                    </td>
                                    <td>
                                        <StatusBadge status={order.status} />
                                    </td>
                                    <td className={styles.approvedByCell}>
                                        {order.approvedBy || '-'}
                                        {order.approvedAt && (
                                            <div className={styles.approvedDateCell}>
                                                {typeof order.approvedAt === 'object' ? order.approvedAt.toLocaleDateString?.('pt-BR') : order.approvedAt}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            <button
                                                onClick={() => handlePrintOrder(order)}
                                                className={`${styles.actionButton} ${styles.printButton}`}
                                                disabled={actionLoading}
                                                title="Imprimir"
                                            >
                                                <FiPrinter size={14} />
                                            </button>
                                            {can.view && (
                                                <button
                                                    onClick={() => setViewingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.viewButton}`}
                                                    disabled={actionLoading}
                                                    title="Visualizar"
                                                >
                                                    <FiEye size={14} />
                                                </button>
                                            )}
                                            {can.track && (
                                                <button
                                                    onClick={() => setTrackingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.trackButton}`}
                                                    disabled={actionLoading}
                                                    title="Rastrear"
                                                >
                                                    <FiMapPin size={14} />
                                                </button>
                                            )}
                                            {can.approve && order.status === ORDER_STATUS.PENDING && (
                                                <button
                                                    onClick={() => setApprovingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.confirmButton}`}
                                                    disabled={actionLoading}
                                                    title="Aprovar/Rejeitar"
                                                >
                                                    <FiCheck size={14} />
                                                </button>
                                            )}
                                            {order.status === ORDER_STATUS.REJECTED && order.approvalNotes && (
                                                <span className={styles.reviewRequired} title={order.approvalNotes}>
                                                    <FiAlertCircle size={14} style={{ color: '#dc3545' }} />
                                                    <span style={{ fontSize: '0.65rem', marginLeft: '4px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {order.approvalNotes.length > 25 ? order.approvalNotes.substring(0, 25) + '...' : order.approvalNotes}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7">
                                    <EmptyState
                                        title={searchTerm || statusFilter ? 'Nenhum pedido encontrado com os filtros aplicados.' : 'Nenhum pedido cadastrado.'}
                                        message={searchTerm || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo pedido de compra.'}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!showSkeleton && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOrders.length}
                    onPageChange={handlePageChange}
                    disabled={actionLoading}
                    itemsLabel="pedidos"
                />
            )}
        </div>
    );
};

export default PurchaseOrderApprovalPage;